import { useTranslation } from 'react-i18next';
import { SubscriptionStatusBadge } from '@/modules/subscriptions/components/SubscriptionStatusBadge';
import { Button } from '@/shared/components/ui/button';
import { Icons } from '@/shared/components/ui/icons';
import type { AdminTrialItem } from '../types/adminTrial';
import { formatDate } from '../utils/trialHelpers';
import { EntitlementStatusBadge } from './EntitlementStatusBadge';
import { TrialDaysBadge } from './TrialDaysBadge';

interface TrialMobileCardProps {
  trial: AdminTrialItem;
  locale: string;
  onViewDetails: () => void;
}

export function TrialMobileCard({ trial, locale, onViewDetails }: TrialMobileCardProps) {
  const { t } = useTranslation();
  const unavailable = t('trials.identity.unavailable');

  return (
    <article className="rounded-xl border border-border bg-card p-4 shadow-sm space-y-3">
      <div className="flex items-start justify-between gap-3 min-w-0">
        <div className="min-w-0 flex-1 space-y-1">
          <p
            className="text-base font-semibold text-foreground leading-snug truncate"
            title={trial.customer_name ?? undefined}
          >
            {trial.customer_name ?? unavailable}
          </p>
          <p className="text-sm text-muted-foreground truncate" title={trial.tenant_name}>
            {trial.tenant_name}
          </p>
        </div>
        <SubscriptionStatusBadge status={trial.subscription_status} />
      </div>

      <dl className="space-y-1.5 text-sm min-w-0">
        <div className="min-w-0">
          <dt className="text-xs font-medium text-muted-foreground">
            {t('trials.columns.customerEmail')}
          </dt>
          <dd className="text-foreground break-all">
            {trial.customer_email ?? (
              <span className="text-muted-foreground italic">{unavailable}</span>
            )}
          </dd>
        </div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <div>
            <dt className="text-xs font-medium text-muted-foreground inline">
              {t('trials.columns.product')}
            </dt>
            <dd className="text-foreground inline ms-1">{trial.product_name}</dd>
          </div>
          <span className="text-muted-foreground" aria-hidden>
            ·
          </span>
          <div>
            <dt className="text-xs font-medium text-muted-foreground inline">
              {t('trials.columns.plan')}
            </dt>
            <dd className="text-foreground inline ms-1">{trial.plan_name}</dd>
          </div>
        </div>
        {trial.customer_phone ? (
          <div>
            <dt className="text-xs font-medium text-muted-foreground">
              {t('trials.columns.customerPhone')}
            </dt>
            <dd className="text-foreground whitespace-nowrap">{trial.customer_phone}</dd>
          </div>
        ) : null}
      </dl>

      <div className="flex flex-wrap items-center gap-2">
        <TrialDaysBadge trial={trial} />
        <EntitlementStatusBadge status={trial.entitlement_status} />
      </div>

      <p className="text-xs text-muted-foreground tabular-nums">
        {formatDate(trial.trial_started_at, locale)} – {formatDate(trial.trial_ends_at, locale)}
      </p>

      <Button variant="default" size="sm" className="w-full" onClick={onViewDetails}>
        <Icons.Eye className="h-4 w-4" />
        {t('trials.actions.viewDetails')}
      </Button>
    </article>
  );
}
